import { Customer, NoteType, PostBulkNoteAdd, PostNoteAdd, QuerySourceDatas, SourceDataEventStatus, SourceDataFormStatus } from "@/api/dtos";
import { AppSettings } from "@/AppSettings";
import InputOnlyField from "@/components/forms/fields/inputOnly/InputOnlyField";
import Form from "@/components/forms/Form";
import FormSubmitButton from "@/components/forms/FormSubmitButton";
import Autocomplete from "@/components/forms/inputs/Autocomplete";
import AutocompleteMultiple from "@/components/forms/inputs/AutocompleteMultiple";
import Multiselect from "@/components/forms/inputs/Multiselect";
import Numeric from "@/components/forms/inputs/Numeric";
import Select, { ISelectItem } from "@/components/forms/inputs/Select";
import Text from "@/components/forms/inputs/Text";
import BasicAlert from "@/components/layouts/BasicAlert";
import IsLoading from "@/components/layouts/IsLoading";
import SetLinks from "@/components/layouts/SetLinks";
import Show from "@/components/layouts/Show";
import NotesAndQueriesList from "@/components/lists/notes/NotesAndQueriesList";
import ServiceStackClientContext from "@/contexts/api/ServiceStackClientContext";
import AuthenticatedUserContext from "@/contexts/authentication/AuthenticatedUserContext";
import CoordinatingGroupContext from "@/contexts/data/CoordinatingGroupContext";
import CoordinatingGroupsContext from "@/contexts/data/CoordinatingGroupsContext";
import InstitutionContext from "@/contexts/data/InstitutionContext";
import InstitutionsContext from "@/contexts/data/InstitutionsContext";
import MasterGroupContext from "@/contexts/data/MasterGroupContext";
import MasterGroupsContext from "@/contexts/data/MasterGroupsContext";
import MetadataContext from "@/contexts/data/MetadataContext";
import ParticipantsContext from "@/contexts/data/ParticipantsContext";
import TrialConfigurationSourceDataContext from "@/contexts/data/TrialConfigurationSourceDataContext";
import TrialContext from "@/contexts/data/TrialContext";
import TrialsContext from "@/contexts/data/TrialsContext";
import { BulkAddNotesLink, NotesAndQueriesInstitutionCreateLink, NotesAndQueriesParticipantCreateLink, NotesAndQueriesTrialCreateLink } from "@/contexts/layouts/LinkContext";
import { FormError } from "@/errors";
import useTrialNotesAndQueries from "@/hooks/data/useTrialNotesAndQueries";
import FormErrorType from "@/types/form/FormErrorType";
import noteTypeToText from "@/utilities/configuration/noteTypeToText";
import { LoadingButton } from "@mui/lab";
import { Box, Button, Paper, styled, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { Condition, ConditionMode, IFormState, IFormSubmit, IFormSubmitFailed, IFormSubmitValidationFailed, IFormValidate, useScopedField, ValidateOn } from "@ngt/forms-core";
import { useSnackbar } from "notistack";
import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";



const FormButtons = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'flex-end'

}));

enum EventType {
    All = 1,
    Latest = 2,
    Specific = 3,
    LatestComplete = 4,
    LatestFormComplete = 5
}

const eventTypeOptions = [
    // {
    //     id: EventType.Latest,
    //     name: 'All',
    //     description: 'All events where the form appears'
    // },
    {
        id: EventType.Latest,
        name: 'Latest Event',
        description: 'The latest event where the form appears'
    },
    {
        id: EventType.LatestComplete,
        name: 'Latest Complete Event',
        description: 'The latest complete event where the form appears'
    },
    {
        id: EventType.LatestFormComplete,
        name: 'Latest Event With Complete Form',
        description: 'The latest event where the form appears and is complete'
    },
    {
        id: EventType.Specific,
        name: 'Specific Event',
        description: 'The specific selected event and repeat.'
    }
]


const adminTypeOptions = [
    AppSettings.noteTypes[NoteType.Annotation] ? 
        {
            id: NoteType.Annotation,
            name: noteTypeToText(NoteType.Annotation)
        } :
        undefined,
    AppSettings.noteTypes[NoteType.InternalQuery] ? 
        {
            id: NoteType.InternalQuery,
            name: noteTypeToText(NoteType.InternalQuery)
        } :
        undefined,
    AppSettings.noteTypes[NoteType.ProtocolDeviation] ? 
        {
            id: NoteType.ProtocolDeviation,
            name: noteTypeToText(NoteType.ProtocolDeviation)
        } :
        undefined,
    AppSettings.noteTypes[NoteType.ImportantProtocolDeviation] ? 
        {
            id: NoteType.ImportantProtocolDeviation,
            name: noteTypeToText(NoteType.ImportantProtocolDeviation)
        } :
        undefined,
    AppSettings.noteTypes[NoteType.ReasonForChange] ? 
        {
            id: NoteType.ReasonForChange,
            name: noteTypeToText(NoteType.ReasonForChange)
        } :
        undefined,
    AppSettings.noteTypes[NoteType.SiteQuery] ? 
        {
            id: NoteType.SiteQuery,
            name: noteTypeToText(NoteType.SiteQuery)
        } :
        undefined,
    AppSettings.noteTypes[NoteType.System] ? 
        {
            id: NoteType.System,
            name: noteTypeToText(NoteType.System)
        } :
        undefined,
].filter(x => !!x) as Array<{ id: NoteType; name: string; }>

const typeOptions = [
    AppSettings.noteTypes[NoteType.SiteQuery] ? 
        {
            id: NoteType.SiteQuery,
            name: noteTypeToText(NoteType.SiteQuery)
        } :
        undefined,
    AppSettings.noteTypes[NoteType.Annotation] ? 
        {
            id: NoteType.Annotation,
            name: noteTypeToText(NoteType.Annotation)
        } :
        undefined,
    AppSettings.noteTypes[NoteType.ProtocolDeviation] ? 
        {
            id: NoteType.ProtocolDeviation,
            name: noteTypeToText(NoteType.ProtocolDeviation)
        } :
        undefined,
    AppSettings.noteTypes[NoteType.ImportantProtocolDeviation] ? 
        {
            id: NoteType.ImportantProtocolDeviation,
            name: noteTypeToText(NoteType.ImportantProtocolDeviation)
        } :
        undefined
].filter(x => !!x) as Array<{ id: NoteType; name: string; }>

interface IForm {
    trialId?: number;
    participantIds?: number[];
    eventType?: EventType;
    studyEventOid?: string;
    studyEventRepeat?: number;
    caseReportFormOid?: string;
    itemGroupName?: string;
    itemGroupRepeat?: number;
    itemName?: string;
    noteType?: NoteType;
    noteTitle?: string;
    noteDetails?: string;
}

const isSpecificEventCondition: (state: IFormState<IForm, FormError>, parentPropertyPath?: string | null) => boolean = ({ value }) => {
    return value?.eventType === EventType.Specific;
}

const hasCaseReportFormOid: (state: IFormState<IForm, FormError>, parentPropertyPath?: string | null) => boolean = ({ value }) => {
    return !!value?.caseReportFormOid;
}

const hasItemGroupName: (state: IFormState<IForm, FormError>, parentPropertyPath?: string | null) => boolean = ({ value }) => {
    return !!value?.itemGroupName;
}

const TrialNotesAndQueriesBulkAddInternalForm: FC = () => {
    const { data: trial } = useContext(TrialContext);
    const { data: masterGroups } = useContext(MasterGroupsContext);
    const { data: coordinatingGroups } = useContext(CoordinatingGroupsContext);
    const { data: institutions } = useContext(InstitutionsContext);
    const { data: participants } = useContext(ParticipantsContext);
    const { data: metadata } = useContext(MetadataContext);
    const { data: trialConfiguration } = useContext(TrialConfigurationSourceDataContext);
    const navigate = useNavigate();

    const user = useContext(AuthenticatedUserContext);

    const isAdmin = useMemo(() => {
        return user.data?.roles?.includes('Admin');
    }, [user])

    const returnUrl = useMemo(() => {
        return `/${trial?.oid}/notes-and-queries`
    }, [trial])

    const participantOptions = useMemo(() => {
        return participants?.map(x => ({
            id: x.id,
            value: x.studyNumber ?? ''
        }))
    }, [participants])

    const studyEventOptions = useMemo(() => {
        if (!trialConfiguration?.events) {
            return undefined;
        }
        

        return Object.keys(trialConfiguration.events).map(key => ({
            id: trialConfiguration.events![key].studyEventOid,
            name: trialConfiguration.events![key].name ?? metadata?.events?.[key].name
        })).sort((a, b) => a.name! < b.name! ? -1 : 1)
    }, [metadata, trialConfiguration])

    const { state: { value: studyEventOid }} = useScopedField<string>('studyEventOid', {value: true});
    const { state: { value: caseReportFormOid }} = useScopedField<string>('caseReportFormOid', {value: true});
    const { state: { value: itemGroupName }} = useScopedField<string>('itemGroupName', {value: true});

    const formOptions = useMemo(() => {
        if (!trialConfiguration?.forms) {
            return undefined;
        }

        const options: ISelectItem[] = [];
        const existsMapping: Record<string, boolean> = {};

        for (let eventKey of Object.keys(trialConfiguration.forms)) {
            if (studyEventOid && eventKey != studyEventOid) {
                continue;
            }


            for (let formKey of Object.keys(trialConfiguration.forms[eventKey])) {
                if (existsMapping[formKey]) {
                    continue;
                }

                existsMapping[formKey] = true;

                options.push({
                    id: trialConfiguration.forms[eventKey][formKey].caseReportFormOid,
                    name: trialConfiguration.forms[eventKey][formKey].name ?? metadata?.forms?.[formKey].name
                })
            }
        }
        

        return options.sort((a, b) => a.name! < b.name! ? -1 : 1)
    }, [metadata, trialConfiguration, studyEventOid])

    const itemGroupOptions = useMemo(() => {
        if (!trialConfiguration?.itemGroups || !caseReportFormOid) {
            return undefined;
        }

        const options: ISelectItem[] = [];
        const existsMapping: Record<string, boolean> = {};

        
        for (let eventKey of Object.keys(trialConfiguration.itemGroups)) {
            if (studyEventOid && eventKey != studyEventOid) {
                continue;
            }


            for (let formKey of Object.keys(trialConfiguration.itemGroups[eventKey])) {
                if (caseReportFormOid && formKey != caseReportFormOid) {
                    continue;
                }

                for (let formVersionKey of Object.keys(trialConfiguration.itemGroups[eventKey][formKey])) {
                    for (let itemGroupKey of Object.keys(trialConfiguration.itemGroups[eventKey][formKey][formVersionKey])) {
                        if (existsMapping[itemGroupKey]) {
                            continue;
                        }

                        existsMapping[itemGroupKey] = true;

                        
                        options.push({
                            id: trialConfiguration.itemGroups[eventKey][formKey][formVersionKey][itemGroupKey].itemGroupName,
                            name: trialConfiguration.itemGroups[eventKey][formKey][formVersionKey][itemGroupKey].name ?? metadata?.itemGroups?.[formKey]?.[formVersionKey]?.[itemGroupKey]?.name
                        })
                    }
                }
            }
        }

        return options;
    }, [metadata, trialConfiguration, studyEventOid, caseReportFormOid])

    

    const itemNameOptions = useMemo(() => {
        if (!trialConfiguration?.items || !caseReportFormOid) {
            return undefined;
        }

        const options: ISelectItem[] = [];
        const existsMapping: Record<string, boolean> = {};

        
        for (let eventKey of Object.keys(trialConfiguration.items)) {
            if (studyEventOid && eventKey != studyEventOid) {
                continue;
            }


            for (let formKey of Object.keys(trialConfiguration.items[eventKey])) {
                if (caseReportFormOid && formKey != caseReportFormOid) {
                    continue;
                }

                for (let formVersionKey of Object.keys(trialConfiguration.items[eventKey][formKey])) {
                    for (let itemGroupKey of Object.keys(trialConfiguration.items[eventKey][formKey][formVersionKey])) {
                        if (itemGroupName && itemGroupKey != itemGroupName) {
                            continue;
                        }

                        if (!itemGroupName && itemGroupKey != AppSettings.metadata.itemGroupUngroupedKey) {
                            continue;
                        }

                        for (let itemKey of  Object.keys(trialConfiguration.items[eventKey][formKey][formVersionKey][itemGroupKey])) {
                            if (existsMapping[itemKey]) {
                                continue;
                            }

                            existsMapping[itemKey] = true;

                            
                            options.push({
                                id: trialConfiguration.items[eventKey][formKey][formVersionKey][itemGroupKey][itemKey].itemName,
                                name: trialConfiguration.items[eventKey][formKey][formVersionKey][itemGroupKey][itemKey].name ?? metadata?.items?.[formKey]?.[formVersionKey]?.[itemGroupKey]?.[itemKey]?.name
                            })
                        }
                    }
                }
            }
        }

        return options;
    }, [metadata, trialConfiguration, studyEventOid, caseReportFormOid, itemGroupName])

    return (
        <>
            <Paper sx={{p: 2}}>   
                <InputOnlyField
                    propertyPath="participantIds"
                    label="Participants"
                    TextFieldProps={{
                        margin: 'normal',
                        helperText: 'The participants to attach the note or query to.'
                    }}
                    component={AutocompleteMultiple}
                    disableCloseOnSelect
                    options={participantOptions}
                />   
                <InputOnlyField
                    propertyPath="eventType"
                    label="Study Event Type"
                    helperText="The method used to find the study events that contain the item to attach the note or query to."
                    FormControlProps={{
                        margin: 'normal'
                    }}
                    component={Select}
                    options={eventTypeOptions}
                />
                <Condition
                    condition={isSpecificEventCondition}
                    subscription={{value: true}}
                    mode={ConditionMode.Show}
                > 
                    <InputOnlyField
                        propertyPath="studyEventOid"
                        label="Study Event"
                        helperText="The study event that contains the item to attach the note or query to."
                        FormControlProps={{
                            margin: 'normal'
                        }}
                        component={Select}
                        options={studyEventOptions}
                    />  
                    <InputOnlyField
                        propertyPath="studyEventRepeat"
                        label="Study Event Repeat"
                        helperText="The repeat of the study event that contains the item to attach the note or query to."
                        margin="normal"
                        component={Numeric}
                        numberFormat={{
                            decimalScale: 0
                        }}
                    />  
                </Condition>
                <InputOnlyField
                    propertyPath="caseReportFormOid"
                    label="Case Report Form"
                    helperText="The case report form that contains the item to attach the note or query to."
                    FormControlProps={{
                        margin: 'normal'
                    }}
                    component={Select}
                    options={formOptions}
                /> 
                <Condition
                    condition={hasCaseReportFormOid}
                    subscription={{value: true}}
                    mode={ConditionMode.Show}
                > 
                    <InputOnlyField
                        propertyPath="itemGroupName"
                        label="Item Group"
                        helperText="The item group that contains the item to attach the note or query to."
                        FormControlProps={{
                            margin: 'normal'
                        }}
                        component={Select}
                        options={itemGroupOptions}
                    />
                    <Condition
                        condition={hasItemGroupName}
                        subscription={{value: true}}
                        mode={ConditionMode.Show}
                    > 
                        <InputOnlyField
                            propertyPath="itemGroupRepeat"
                            label="Item Group Repeat"
                            helperText="The repeat of the item group that contains the item to attach the note or query to."
                            margin="normal"
                            component={Numeric}
                            numberFormat={{
                                decimalScale: 0
                            }}
                        />
                    </Condition>
                    <InputOnlyField
                        propertyPath="itemName"
                        label="Item"
                        helperText="The item to attach the note or query to."
                        FormControlProps={{
                            margin: 'normal'
                        }}
                        component={Select}
                        options={itemNameOptions}
                    />  
                </Condition>    
                <InputOnlyField
                    propertyPath="noteType"
                    label="Type"
                    helperText="The type of note or query to be added."
                    FormControlProps={{
                        margin: 'normal'
                    }}
                    component={Select}
                    options={isAdmin ? adminTypeOptions : typeOptions}
                /> 
                <NoteDescriptionSelector />    
                <InputOnlyField
                    propertyPath="noteDetails"
                    label="Detailed Note"
                    helperText="The complete details of the note or query."
                    margin="normal"
                    component={Text}
                    multiline
                    minRows={2}
                />   
            </Paper>  
            <FormButtons>
                <FormSubmitButton
                    variant="contained"
                    color="primary"
                >
                    Process
                </FormSubmitButton>
                <Button
                    variant="contained"
                    color="inherit"
                    onClick={() => navigate(returnUrl)}
                >
                    Cancel
                </Button>
            </FormButtons>  
        </>
    )
}

interface INoteData {
    error?: string;
    colspan?: number;
    note: PostNoteAdd;
}


const NoteDescriptionSelector: FC = () => {
    const { state: { value: noteType } } = useScopedField<NoteType>('noteType', { value: true });
    const { state: { value: noteTitleId }, context: { setValue: setTitleId} } = useScopedField('noteTitleId', { value: true });
    const { context: { setValue: setNoteTitle }} = useScopedField('noteTitle', { });

    useEffect(() => {
        setTitleId(undefined)
    },  [noteType, setTitleId])

    useEffect(() => {
        if (!noteType) {
            setNoteTitle(undefined);
        }
        else if (!noteTitleId) {
            setNoteTitle(undefined);
        }
        else {
            setNoteTitle(AppSettings.noteTypeTitles[noteType]?.find(x => x.id === noteTitleId)?.name);
        }
    },  [noteTitleId, noteType, setNoteTitle])

    const typeTitleOptions = noteType ? AppSettings.noteTypeTitles[noteType] : undefined;

    return (
        <> 
            {
                typeTitleOptions ?
                    <InputOnlyField
                        propertyPath="noteTitleId"
                        label="Description"
                        helperText="A description of the note or query."
                        FormControlProps={{
                            margin: 'normal'
                        }}
                        nullOption={false}
                        component={Select}
                        options={typeTitleOptions}
                    /> : 
                    <InputOnlyField
                        propertyPath="noteTitle"
                        label="Description"
                        helperText="A description of the note or query."
                        margin="normal"
                        component={Text}
                    />  
            }
        </>
    )
}

const TrialNotesAndQueriesBulkAdd: FC = () => {
    const { idMap: trialMap } = useContext(TrialsContext);
    const { data: trial } = useContext(TrialContext);
    const { idMap: masterGroupMap } = useContext(MasterGroupsContext);
    const { idMap: coordinatingGroupMap } = useContext(CoordinatingGroupsContext);
    const { idMap: institutionMap } = useContext(InstitutionsContext);
    const { idMap: participantMap } = useContext(ParticipantsContext);
    const { data: metadata } = useContext(MetadataContext);
    const { data: trialConfiguration } = useContext(TrialConfigurationSourceDataContext);
    
    const { enqueueSnackbar } = useSnackbar();
    const client = useContext(ServiceStackClientContext);

    const initialValue = useMemo(() => {
        return {
            trialId: trial?.id
        } as IForm
    }, [trial])

    
    
    const validate = useCallback<IFormValidate<IForm, FormError>>(async (formState, formActions) => {
        let errors: Record<string, FormError[]> = {};

        if (!formState?.value?.participantIds || formState.value.participantIds.length === 0) {
            errors['participantIds'] = [
                {
                    property: 'participantIds',
                    message: 'At least one participant must be selected!',
                    type: FormErrorType.Normal
                }
            ];
        }

        if (!formState?.value?.eventType) {
            errors['eventType'] = [
                {
                    property: 'eventType',
                    message: 'Event Type must be provided!',
                    type: FormErrorType.Normal
                }
            ];
        }

        if (formState?.value?.eventType === EventType.Specific) {
            if (!formState?.value?.studyEventOid) {
                errors['studyEventOid'] = [
                    {
                        property: 'studyEventOid',
                        message: 'Study Event must be provided!',
                        type: FormErrorType.Normal
                    }
                ];
            }

            if (!formState?.value?.studyEventRepeat) {
                errors['studyEventRepeat'] = [
                    {
                        property: 'studyEventRepeat',
                        message: 'Study Event Repeat must be provided!',
                        type: FormErrorType.Normal
                    }
                ];
            }
        }

        
        if (!formState?.value?.caseReportFormOid) {
            errors['caseReportFormOid'] = [
                {
                    property: 'caseReportFormOid',
                    message: 'Case Report Form must be provided!',
                    type: FormErrorType.Normal
                }
            ];
        }

        
        if (formState?.value?.caseReportFormOid) {
            if (!formState?.value?.itemGroupName) {
                // errors['itemGroupName'] = [
                //     {
                //         property: 'itemGroupName',
                //         message: 'Item Group must be provided!',
                //         type: FormErrorType.Normal
                //     }
                // ];
            }

            
            
            if (formState?.value?.itemGroupName) {
                if (!formState?.value?.itemGroupRepeat) {
                    errors['itemGroupRepeat'] = [
                        {
                            property: 'itemGroupRepeat',
                            message: 'Item Group Repeat must be provided!',
                            type: FormErrorType.Normal
                        }
                    ];
                }
            }

            if (!formState?.value?.itemName) {
                errors['itemName'] = [
                    {
                        property: 'itemName',
                        message: 'Item must be provided!',
                        type: FormErrorType.Normal
                    }
                ];
            }
        }

        if (!formState?.value?.noteType) {
            errors['noteType'] = [
                {
                    property: 'noteType',
                    message: 'Type must be provided!',
                    type: FormErrorType.Normal
                }
            ];
        }

        if (!formState?.value?.noteTitle) {
            errors['noteTitle'] = [
                {
                    property: 'noteTitle',
                    message: 'Description must be provided!',
                    type: FormErrorType.Normal
                }
            ];
        }

        const titleOptions = formState?.value?.noteType ? AppSettings.noteTypeTitles[formState?.value?.noteType] : undefined;

        if (titleOptions && !(formState?.value as any)?.noteTitleId) {
            errors['noteTitleId'] = [
                {
                    property: 'noteTitleId',
                    message: 'Description must be provided!',
                    type: FormErrorType.Normal
                }
            ];
        }

        if (!formState?.value?.noteTitle) {
            errors['noteTitle'] = [
                {
                    property: 'noteTitle',
                    message: 'Description must be provided!',
                    type: FormErrorType.Normal
                }
            ];
        }

        return errors;
    }, []);

    const [notes, setNotes] = useState<INoteData[]>();
    const [processingNotes, setProcessingNotes] = useState(false);
    const [addingNotes, setAddingNotes] = useState(false);

    const addNotes = useCallback(async () => {
        setAddingNotes(true);

        try {
            await client.post(new PostBulkNoteAdd({
                notes: notes?.filter(x => !x.error).map(x => x.note)
            }))

            enqueueSnackbar(`Notes successfully created!`, { variant: "success" });
        }
        catch (error) {
            enqueueSnackbar(`An error occurred while attempting to save the notes!`, { variant: "error" });
        }
        finally {
            setAddingNotes(false);
        }
    }, [setAddingNotes, notes, client, enqueueSnackbar])

    const submit = useCallback<IFormSubmit<IForm, FormError>>(async (formState, formActions) => {
        //await client.post(new PostNoteAdd({ ...formState.value }));

        setNotes([])
        setProcessingNotes(true);

        for (let participantId of formState.value!.participantIds!) {
            const participant = participantMap?.[participantId]

            if (!participant) {
                setNotes(c => {
                    return [
                        ...(c ?? []), 
                        {
                            colspan: 12,
                            error: 'Participant could not be found',
                            note: new PostNoteAdd({
                                participantId: participantId,
                            })
                        }
                    ]
                })

                continue;
            }

            const sourceDataResponse = await client.get(new QuerySourceDatas({ trialId: participant.trialId, participantId: participant.id }));

            if (formState.value!.eventType === EventType.All) {
                const note = new PostNoteAdd({
                    trialId: participant.trialId,
                    masterGroupId: participant.masterGroupId,
                    coordinatingGroupId: participant.coordinatingGroupId,
                    institutionId: participant.institutionId,
                    participantId: participant.id,
                    studyEventOid: undefined,
                    studyEventRepeat: undefined,
                    caseReportFormOid: formState.value!.caseReportFormOid,
                    caseReportFormVersionOid: undefined,
                    itemGroupName: formState.value!.itemGroupName,
                    itemGroupRepeat: formState.value!.itemGroupRepeat ?? 1,
                    itemName: formState.value!.itemName,
                    title: formState.value!.noteTitle,
                    details: formState.value!.noteDetails,
                    type: formState.value!.noteType
                });

                setNotes(c => {
                    return [
                        ...(c ?? []), 
                        {
                            issue: 'Event Type "All" has not yet been implemented',
                            colspan: 7,
                            note
                        }
                    ]
                })
            }
            else if (
                formState.value!.eventType === EventType.Latest ||
                formState.value!.eventType === EventType.LatestComplete ||
                formState.value!.eventType === EventType.LatestFormComplete
            ) {
                const note = new PostNoteAdd({
                    trialId: participant.trialId,
                    masterGroupId: participant.masterGroupId,
                    coordinatingGroupId: participant.coordinatingGroupId,
                    institutionId: participant.institutionId,
                    participantId: participant.id,
                    studyEventOid: undefined,
                    studyEventRepeat: undefined,
                    caseReportFormOid: formState.value!.caseReportFormOid,
                    caseReportFormVersionOid: undefined,
                    itemGroupName: formState.value!.itemGroupName,
                    itemGroupRepeat: formState.value!.itemGroupRepeat ?? 1,
                    itemName: formState.value!.itemName,
                    title: formState.value!.noteTitle,
                    details: formState.value!.noteDetails,
                    type: formState.value!.noteType
                });

                if (!metadata?.eventForms) {
                    setNotes(c => {
                        return [
                            ...(c ?? []), 
                            {
                                error: 'No Case Report Form to Study Event mapping is available',
                                colspan: 7,
                                note
                            }
                        ]
                    })

                    continue;
                }

                const studyEventOids: string[] = [];

                for (let studyEventKey of Object.keys(metadata.eventForms))
                {
                    if (!metadata.eventForms[studyEventKey]?.[note.caseReportFormOid!]) {
                        continue;
                    }

                    studyEventOids.push(studyEventKey);
                }

                if (studyEventOids.length == 0) {
                    setNotes(c => {
                        return [
                            ...(c ?? []), 
                            {
                                error: 'Case Report Form is not attached to any Study Event',
                                colspan: 7,
                                note
                            }
                        ]
                    })

                    continue;
                }

                const selectedStudyEvent: {
                    studyEventOid?: string;
                    studyEventRepeat?: number;
                    studyEventStartDate?: string;
                } = {
                    studyEventOid: undefined,
                    studyEventRepeat: undefined,
                    studyEventStartDate: undefined
                }

                for (let studyEventOid of studyEventOids) {
                    const currentStudyEvents = sourceDataResponse.data?.events?.[studyEventOid];

                    if (!currentStudyEvents) {
                        continue;
                    }

                    for (let studyEventRepeat of Object.keys(currentStudyEvents)) {
                        if (!currentStudyEvents[studyEventRepeat as any].startDate) {
                            continue;
                        }

                        if (selectedStudyEvent.studyEventStartDate && selectedStudyEvent.studyEventStartDate > currentStudyEvents[studyEventRepeat as any]?.startDate!) {
                            continue;
                        }
                        
                        if (formState.value!.eventType === EventType.LatestComplete && currentStudyEvents[studyEventRepeat as any]?.status !== SourceDataEventStatus.Complete) {
                            continue;
                        }

                        if (formState.value!.eventType === EventType.LatestFormComplete && 
                            sourceDataResponse.data?.forms?.[studyEventOid!]?.[+studyEventRepeat!]?.[formState.value!.caseReportFormOid!]?.status !== SourceDataFormStatus.Complete
                        ) {
                            continue;
                        }


                        selectedStudyEvent.studyEventOid = studyEventOid;
                        selectedStudyEvent.studyEventRepeat = +studyEventRepeat;
                        selectedStudyEvent.studyEventStartDate = currentStudyEvents[studyEventRepeat as any].startDate;
                    }
                }

                if (!selectedStudyEvent.studyEventOid) {
                    setNotes(c => {
                        return [
                            ...(c ?? []), 
                            {
                                error: 'Target Study Event does not exist on this participant',
                                colspan: 7,
                                note
                            }
                        ]
                    })

                    continue;
                }

                note.studyEventOid = selectedStudyEvent.studyEventOid;
                note.studyEventRepeat = selectedStudyEvent.studyEventRepeat;

                if (!sourceDataResponse.data?.forms?.[note.studyEventOid!]?.[note.studyEventRepeat!]?.[formState.value!.caseReportFormOid!]) {
                    setNotes(c => {
                        return [
                            ...(c ?? []), 
                            {
                                error: 'Target Case Report Form does not exist on the Target Study Event for this participant',
                                colspan: 5,
                                note
                            }
                        ]
                    })

                    continue;
                }

                note.caseReportFormVersionOid = sourceDataResponse.data?.forms?.[note.studyEventOid!]?.[note.studyEventRepeat!]?.[note.caseReportFormOid!].caseReportFormVersionOid;


                if (!sourceDataResponse.data?.items?.[note.studyEventOid!]?.[note.studyEventRepeat!]?.[note.caseReportFormOid!]?.[note.itemGroupName ?? AppSettings.metadata.itemGroupUngroupedKey]?.[note.itemGroupRepeat ?? 1]?.[note.itemName!]) {
                    setNotes(c => {
                        return [
                            ...(c ?? []), 
                            {
                                error: 'Target Item does not exist on Target Case Report Form on the Target Study Event for this participant',
                                colspan: 1,
                                note
                            }
                        ]
                    })

                    continue;
                }

                setNotes(c => {
                    return [
                        ...(c ?? []), 
                        {
                            note
                        }
                    ]
                })
            }
            else { 
                const note = new PostNoteAdd({
                    trialId: participant.trialId,
                    masterGroupId: participant.masterGroupId,
                    coordinatingGroupId: participant.coordinatingGroupId,
                    institutionId: participant.institutionId,
                    participantId: participant.id,
                    studyEventOid: formState.value!.studyEventOid,
                    studyEventRepeat: formState.value!.studyEventRepeat,
                    caseReportFormOid: formState.value!.caseReportFormOid,
                    caseReportFormVersionOid: undefined,
                    itemGroupName: formState.value!.itemGroupName,
                    itemGroupRepeat: formState.value!.itemGroupRepeat ?? 1,
                    itemName: formState.value!.itemName,
                    title: formState.value!.noteTitle,
                    details: formState.value!.noteDetails,
                    type: formState.value!.noteType
                });

                if (!sourceDataResponse.data?.events?.[note.studyEventOid!]?.[note.studyEventRepeat!]) {
                    setNotes(c => {
                        return [
                            ...(c ?? []), 
                            {
                                error: 'Target Study Event does not exist on this participant',
                                colspan: 7,
                                note
                            }
                        ]
                    })

                    continue;
                }

                if (!sourceDataResponse.data?.forms?.[note.studyEventOid!]?.[note.studyEventRepeat!]?.[formState.value!.caseReportFormOid!]) {
                    setNotes(c => {
                        return [
                            ...(c ?? []), 
                            {
                                error: 'Target Case Report Form does not exist on the Target Study Event for this participant',
                                colspan: 5,
                                note
                            }
                        ]
                    })

                    continue;
                }

                note.caseReportFormVersionOid = sourceDataResponse.data?.forms?.[note.studyEventOid!]?.[note.studyEventRepeat!]?.[note.caseReportFormOid!].caseReportFormVersionOid;


                if (!sourceDataResponse.data?.items?.[note.studyEventOid!]?.[note.studyEventRepeat!]?.[note.caseReportFormOid!]?.[note.itemGroupName ?? AppSettings.metadata.itemGroupUngroupedKey]?.[note.itemGroupRepeat ?? 1]?.[note.itemName!]) {
                    setNotes(c => {
                        return [
                            ...(c ?? []), 
                            {
                                error: 'Target Item does not exist on Target Case Report Form on the Target Study Event for this participant',
                                colspan: 1,
                                note
                            }
                        ]
                    })

                    continue;
                }

                setNotes(c => {
                    return [
                        ...(c ?? []), 
                        {
                            note
                        }
                    ]
                })
            }
        }

        setProcessingNotes(false);
        enqueueSnackbar(`Notes successfully processed, see below to complete note creation!`, { variant: "success" });
        
        return {};
    }, [client, enqueueSnackbar, setNotes, participantMap, metadata, setProcessingNotes]);

    const submitFailed = useCallback<IFormSubmitFailed<IForm, FormError>>(async (formState, formActions) => {
        
        enqueueSnackbar(`An error occurred while attempting to process the notes!`, { variant: "error" });

        return {};
    }, [enqueueSnackbar]);

    const submitValidationFailed = useCallback<IFormSubmitValidationFailed<IForm, FormError>>(async (formState, formActions, validationException) => {
        enqueueSnackbar("The form contains invalid data, please correct the marked fields and try again!", { variant: "warning" });

        return formState.errors;
    }, [enqueueSnackbar]);


    const loading = false;
    const validating = false;

    return (
        <>
            <SetLinks 
                createTrialLink={BulkAddNotesLink} 
                createInstitutionLink={BulkAddNotesLink} 
                createParticipantLink={BulkAddNotesLink}  
            />
            <IsLoading name="trial-notes-bulk-add-page" loading={loading} validating={validating} />
            <Show show={!loading}>
                <Typography variant="h1" gutterBottom>
                    Notes And Queries - Bulk Add
                </Typography>
                <Form
                    initialValue={initialValue}
                    checkForClasses={false}
                    debug={AppSettings.debug}
                    onSubmit={submit}
                    onSubmitFailed={submitFailed}
                    onSubmitValidationFailed={submitValidationFailed}
                    onValidate={validate}
                    validateOn={ValidateOn.onChange}
                >
                    <TrialNotesAndQueriesBulkAddInternalForm />
                </Form>
                {
                    notes && (
                        <>
                            <Paper>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>
                                                Trial
                                            </TableCell>
                                            <TableCell>
                                                Master Group
                                            </TableCell>
                                            <TableCell>
                                                Coordinating Group
                                            </TableCell>
                                            <TableCell>
                                                Institution
                                            </TableCell>
                                            <TableCell>
                                                Participant
                                            </TableCell>
                                            <TableCell>
                                                Study Event
                                            </TableCell>
                                            <TableCell>
                                                Study Event Repeat
                                            </TableCell>
                                            <TableCell>
                                                Case Report Form
                                            </TableCell>
                                            <TableCell>
                                                Case Report Form Version
                                            </TableCell>
                                            <TableCell>
                                                Item Group
                                            </TableCell>
                                            <TableCell>
                                                Item Group Repeat
                                            </TableCell>
                                            <TableCell>
                                                Item
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {
                                            notes.map((note, index) => {
                                                return (
                                                    <TableRow
                                                        key={index}
                                                        sx={{
                                                            backgroundColor: note.error ? 'rgba(255, 0, 0, 0.25)' : undefined
                                                        }}
                                                    >
                                                        {
                                                            (!note.colspan || note.colspan < 12) && (
                                                                <TableCell>
                                                                    {note.note.trialId && (trialMap?.[note.note.trialId]?.name ?? note.note.trialId)}
                                                                </TableCell>
                                                            )
                                                        }
                                                        {
                                                            (!note.colspan || note.colspan < 11) && (
                                                                <TableCell>
                                                                    {note.note.masterGroupId && (masterGroupMap?.[note.note.masterGroupId]?.name ?? note.note.masterGroupId)}
                                                                </TableCell>
                                                            )
                                                        }
                                                        {
                                                            (!note.colspan || note.colspan < 10) && (
                                                                <TableCell>
                                                                    {note.note.coordinatingGroupId && (coordinatingGroupMap?.[note.note.coordinatingGroupId]?.name ?? note.note.coordinatingGroupId)}
                                                                </TableCell>
                                                            )
                                                        }
                                                        {
                                                            (!note.colspan || note.colspan < 9) && (
                                                                <TableCell>
                                                                    {note.note.institutionId && (institutionMap?.[note.note.institutionId]?.name ?? note.note.institutionId)}
                                                                </TableCell>
                                                            )
                                                        }
                                                        {
                                                            (!note.colspan || note.colspan < 8) && (
                                                                <TableCell>
                                                                    {note.note.participantId && (participantMap?.[note.note.participantId]?.studyNumber ?? note.note.participantId)}
                                                                </TableCell>
                                                            )
                                                        }
                                                        {
                                                            (!note.colspan || note.colspan < 7) && (
                                                                <TableCell>
                                                                    {note.note.studyEventOid && (trialConfiguration?.events?.[note.note.studyEventOid]?.name ?? metadata?.events?.[note.note.studyEventOid]?.name ?? note.note.studyEventOid)}
                                                                </TableCell>
                                                            )
                                                        }
                                                        {
                                                            (!note.colspan || note.colspan < 6) && (
                                                                <TableCell>
                                                                    {note.note.studyEventRepeat}
                                                                </TableCell>
                                                            )
                                                        }
                                                        {
                                                            (!note.colspan || note.colspan < 5) && (
                                                                <TableCell>
                                                                    {note.note.studyEventOid && note.note.caseReportFormOid && (trialConfiguration?.forms?.[note.note.studyEventOid]?.[note.note.caseReportFormOid]?.name ?? metadata?.forms?.[note.note.caseReportFormOid]?.name ?? note.note.caseReportFormOid)}
                                                                </TableCell>
                                                            )
                                                        }
                                                        {
                                                            (!note.colspan || note.colspan < 4) && (
                                                                <TableCell>
                                                                    {note.note.studyEventOid && note.note.caseReportFormOid && note.note.caseReportFormVersionOid && (trialConfiguration?.formVersions?.[note.note.studyEventOid]?.[note.note.caseReportFormOid]?.[note.note.caseReportFormVersionOid]?.name ?? metadata?.formVersions?.[note.note.caseReportFormOid]?.[note.note.caseReportFormVersionOid]?.name ?? note.note.caseReportFormVersionOid)}
                                                                </TableCell>
                                                            )
                                                        }
                                                        {
                                                            (!note.colspan || note.colspan < 3) && (
                                                                <TableCell>
                                                                    {
                                                                        note.note.studyEventOid && 
                                                                        note.note.caseReportFormOid && 
                                                                        note.note.caseReportFormVersionOid && 
                                                                        note.note.itemGroupName && (
                                                                            trialConfiguration?.itemGroups?.[note.note.studyEventOid]?.[note.note.caseReportFormOid]?.[note.note.caseReportFormVersionOid]?.[note.note.itemGroupName]?.name ?? 
                                                                            metadata?.itemGroups?.[note.note.caseReportFormOid]?.[note.note.caseReportFormVersionOid]?.[note.note.itemGroupName]?.name ?? 
                                                                            note.note.itemGroupName
                                                                        )
                                                                    }
                                                                </TableCell>
                                                            )
                                                        }
                                                        {
                                                            (!note.colspan || note.colspan < 2) && (
                                                                <TableCell>
                                                                    {note.note.itemGroupName && note.note.itemGroupRepeat}
                                                                </TableCell>
                                                            )
                                                        }
                                                        {
                                                            (!note.colspan || note.colspan < 1) && (
                                                                <TableCell>
                                                                    {
                                                                        note.note.studyEventOid && 
                                                                        note.note.caseReportFormOid && 
                                                                        note.note.caseReportFormVersionOid && 
                                                                        note.note.itemName && (
                                                                            trialConfiguration?.items?.[note.note.studyEventOid]?.[note.note.caseReportFormOid]?.[note.note.caseReportFormVersionOid]?.[note.note.itemGroupName ?? AppSettings.metadata.itemGroupUngroupedKey]?.[note.note.itemName]?.name ?? 
                                                                            metadata?.items?.[note.note.caseReportFormOid]?.[note.note.caseReportFormVersionOid]?.[note.note.itemGroupName ?? AppSettings.metadata.itemGroupUngroupedKey]?.[note.note.itemName]?.name ?? 
                                                                            note.note.itemName
                                                                        )
                                                                    }
                                                                </TableCell>
                                                            )
                                                        }
                                                        {
                                                            note.error && (
                                                                <TableCell
                                                                    colSpan={note.colspan ?? 1}
                                                                    sx={{ color: 'error.main'}}
                                                                >
                                                                    {note.error}
                                                                </TableCell>
                                                            )
                                                        }
                                                    </TableRow>
                                                )
                                            })
                                        }
                                    </TableBody>
                                </Table>
                            </Paper>
                            <FormButtons>
                                <BasicAlert description=" Only notes that do not have an error will be added." severity="warning" />
                                <LoadingButton
                                    variant="contained"
                                    color="primary"
                                    loading={processingNotes || addingNotes}
                                    onClick={addNotes}
                                >
                                    Add Notes
                                </LoadingButton>
                            </FormButtons>
                        </>
                    )
                }
            </Show>
        </>
    )
};

export default TrialNotesAndQueriesBulkAdd;